import type { OpenAiDataType } from './utils';

/**
 * Event data object passed as the third argument to `oaiq('measure', ...)`.
 *
 * Only documented fields are included — OpenAI rejects unknown keys.
 */
export interface OpenAiMeasureData {
    type: OpenAiDataType;
    amount?: number;
    currency?: string;
    contents?: Array<{
        id?: string;
        name?: string;
        content_type?: string;
        quantity?: number;
        amount?: number;
        currency?: string;
    }>;
}

/**
 * Builds the ChatGPT Ads event data object from optional tracker payload fields.
 *
 * Monetary values are converted to ISO 4217 minor-unit integers (e.g. $129.99 →
 * 12999). Meta receives decimal `value` strings; OpenAI requires integers and
 * rejects the request when the types disagree.
 */
export function buildOpenAiEventData(
    dataType: OpenAiDataType,
    data: Record<string, unknown> | undefined,
): OpenAiMeasureData {
    const payload: OpenAiMeasureData = { type: dataType };

    const amount = toMinorUnits(data?.['value']);
    const currency = text(data?.['currency']);
    if (amount !== undefined && currency) {
        payload.amount = amount;
        payload.currency = currency.toUpperCase();
    }

    const contentIds = data?.['content_ids'];
    if (Array.isArray(contentIds) && contentIds.length > 0) {
        payload.contents = contentIds
            .map((id) => text(id))
            .filter((id): id is string => Boolean(id))
            .map((id) => ({
                id,
                content_type: 'product',
                quantity: 1,
            }));
    }

    return payload;
}

/**
 * Converts a decimal monetary value into an integer minor-unit amount.
 *
 * Accepts the decimal strings our cart logger already produces (`"12.99"`) and
 * numeric values. Returns undefined when the input cannot be a price.
 */
function toMinorUnits(value: unknown): number | undefined {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return undefined;
    }

    return Math.round(parsed * 100);
}

function text(value: unknown): string | undefined {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined;
    }

    const trimmed = String(value).trim();
    return trimmed === '' ? undefined : trimmed;
}
